/**
 * Attachment Cache Utility for Web
 *
 * Caches attachment data with signed URLs to minimize storage requests.
 * Uses sessionStorage for ephemeral caching (cleared on tab close).
 */

const CACHE_PREFIX = 'att_cache_';
const CACHE_EXPIRY_HOURS = 23; // Cache for 23 hours (signed URLs valid for 24)

interface CachedAttachment {
  [key: string]: any;
  cached_at: number;
  expires_at: number;
}

interface AttachmentCacheData {
  [messageId: string]: CachedAttachment[];
}

// In-memory cache for ultra-fast access
let memoryCache: AttachmentCacheData = {};

/**
 * Check if cached data is still valid
 */
function isExpired(cachedAt: number, expiresAt: number): boolean {
  const now = Date.now();
  return now >= expiresAt;
}

/**
 * Get cache key for a message
 */
function getCacheKey(messageId: string): string {
  return `${CACHE_PREFIX}${messageId}`;
}

/**
 * Get cached attachments for a single message
 */
export function getCachedAttachments(messageId: string): any[] | null {
  // Check memory cache first (fastest)
  if (memoryCache[messageId]) {
    const cached = memoryCache[messageId];
    if (!isExpired(cached[0].cached_at, cached[0].expires_at)) {
      return cached;
    } else {
      // Expired - remove from memory cache
      delete memoryCache[messageId];
    }
  }

  // Check sessionStorage
  try {
    const cacheKey = getCacheKey(messageId);
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const cached: CachedAttachment[] = JSON.parse(cachedData);

      if (cached.length > 0 && !isExpired(cached[0].cached_at, cached[0].expires_at)) {
        // Update memory cache
        memoryCache[messageId] = cached;
        return cached;
      } else {
        // Expired - remove from storage
        sessionStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    console.error('Error reading attachment cache:', error);
  }

  return null;
}

/**
 * Get cached attachments for multiple messages
 */
export function getBulkCachedAttachments(messageIds: string[]): {
  cached: Record<string, any[]>;
  missing: string[];
} {
  const cached: Record<string, any[]> = {};
  const missing: string[] = [];

  for (const messageId of messageIds) {
    const cachedData = getCachedAttachments(messageId);
    if (cachedData) {
      cached[messageId] = cachedData;
    } else {
      missing.push(messageId);
    }
  }

  return { cached, missing };
}

/**
 * Cache attachments for a message
 */
export function cacheAttachments(messageId: string, attachments: any[]): void {
  if (!attachments || attachments.length === 0) {
    return;
  }

  const now = Date.now();
  const expiresAt = now + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000);

  const cachedAttachments: CachedAttachment[] = attachments.map(att => ({
    ...att,
    cached_at: now,
    expires_at: expiresAt,
  }));

  // Update memory cache
  memoryCache[messageId] = cachedAttachments;

  // Update sessionStorage (with error handling for quota exceeded)
  try {
    const cacheKey = getCacheKey(messageId);
    sessionStorage.setItem(cacheKey, JSON.stringify(cachedAttachments));
  } catch (error) {
    // Handle quota exceeded errors gracefully
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('Session storage quota exceeded, clearing old cache');
      clearExpiredCache();
      // Try again after clearing
      try {
        const cacheKey = getCacheKey(messageId);
        sessionStorage.setItem(cacheKey, JSON.stringify(cachedAttachments));
      } catch (retryError) {
        console.error('Still unable to cache after cleanup:', retryError);
      }
    } else {
      console.error('Error writing attachment cache:', error);
    }
  }
}

/**
 * Cache multiple messages' attachments at once
 */
export function cacheBulkAttachments(attachmentsByMessage: Record<string, any[]>): void {
  Object.entries(attachmentsByMessage).forEach(([messageId, attachments]) => {
    cacheAttachments(messageId, attachments);
  });
}

/**
 * Clear expired cache entries
 */
export function clearExpiredCache(): void {
  try {
    const keysToRemove: string[] = [];

    // Iterate through all sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        const cachedData = sessionStorage.getItem(key);
        if (cachedData) {
          try {
            const cached: CachedAttachment[] = JSON.parse(cachedData);
            if (cached.length > 0 && isExpired(cached[0].cached_at, cached[0].expires_at)) {
              keysToRemove.push(key);
            }
          } catch (parseError) {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }
    }

    // Remove expired entries
    keysToRemove.forEach(key => sessionStorage.removeItem(key));

    // Clear expired from memory cache
    Object.keys(memoryCache).forEach(messageId => {
      const cached = memoryCache[messageId];
      if (cached.length > 0 && isExpired(cached[0].cached_at, cached[0].expires_at)) {
        delete memoryCache[messageId];
      }
    });

  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
}

/**
 * Clear all attachment cache
 */
export function clearAllAttachmentCache(): void {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    memoryCache = {};
  } catch (error) {
    console.error('Error clearing all attachment cache:', error);
  }
}

/**
 * Get cache statistics
 */
export function getCacheStats(): {
  totalEntries: number;
  memoryCached: number;
  storageCached: number;
  estimatedSizeKB: number;
} {
  try {
    let storageCachedCount = 0;
    let totalSize = 0;

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        storageCachedCount++;
        const data = sessionStorage.getItem(key);
        if (data) {
          totalSize += data.length * 2; // Rough estimate (UTF-16 encoding)
        }
      }
    }

    return {
      totalEntries: storageCachedCount,
      memoryCached: Object.keys(memoryCache).length,
      storageCached: storageCachedCount,
      estimatedSizeKB: Math.round(totalSize / 1024),
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return {
      totalEntries: 0,
      memoryCached: 0,
      storageCached: 0,
      estimatedSizeKB: 0,
    };
  }
}

// Auto-cleanup expired cache on page load
if (typeof window !== 'undefined') {
  // Run cleanup after a short delay to not block page load
  setTimeout(() => {
    clearExpiredCache();
  }, 2000);
}
